import React from 'react';
import { Link } from 'react-router-dom';
import TikTokIcon from '../common/TikTokIcon';
import InstaIcon from '../common/InstaIcon';
import { buildWhatsAppUrl } from '../../utils/whatsappOrder';
import { usePublicStoreSettings } from '../../hooks/usePublicStoreSettings';

/**
 * Storefront footer — dark, editorial. FW2026 redesign.
 *
 *  Desktop (md+)  — 12-col grid (Brand 4 · Tienda 2 · Ayuda 2 · Pinksisly 4)
 *                   + 5-col trust/payment strip + centered copyright.
 *
 *  Mobile (<md)   — "Action-first + Progressive disclosure" pattern:
 *                   1. Help hero (WhatsApp pill + Email)         ← new, top
 *                   2. TIENDA / AYUDA accordions (<details>)     ← collapsed
 *                   3. Pinksisly contact (always open, 3 lines)
 *                   4. Brand block (compact, lower priority)
 *                   5. Trust 2×2 grid
 *                   6. Payments + © in one band
 *
 *  Accordions use native <details>/<summary> — zero JS, native a11y
 *  (Tab/Enter/Space already wired by the platform), no animation jank.
 *  Marker is hidden via CSS so we can use our own +/− indicator.
 */

/* ──────────────────────────────────────────────────────────────────
 * Inline glyphs
 * ──────────────────────────────────────────────────────────────── */
const WhatsAppGlyph = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884" />
  </svg>
);
const MailGlyph = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect width="20" height="16" x="2" y="4" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);
const PinGlyph = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 10c0 7-8 12-8 12s-8-5-8-12a8 8 0 0116 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

/* ──────────────────────────────────────────────────────────────────
 * Trust badge — icon + 2 lines of label
 * ──────────────────────────────────────────────────────────────── */
const TrustBadge = ({ icon, title, sub }) => (
  <div className="flex items-center gap-3">
    <span
      className="w-8 h-8 rounded-full flex items-center justify-center text-white/80 flex-shrink-0"
      style={{ background: 'rgba(255,255,255,0.06)' }}
    >
      {icon}
    </span>
    <div className="min-w-0">
      <p className="text-[12.5px] text-white leading-tight">{title}</p>
      <p className="text-[11px] text-white/55 leading-tight">{sub}</p>
    </div>
  </div>
);

/* ──────────────────────────────────────────────────────────────────
 * Payment marks — minimal wordmarks (not licensed brand assets).
 * ──────────────────────────────────────────────────────────────── */
const PayMark = ({ bg, fg, label, italic = false }) => (
  <span
    className="inline-flex items-center justify-center select-none flex-shrink-0"
    style={{
      background: bg,
      color: fg,
      width: 46,
      height: 30,
      borderRadius: 5,
      fontFamily: '"Helvetica Neue", Arial, sans-serif',
      fontWeight: 800,
      fontSize: 11,
      letterSpacing: '0.02em',
      fontStyle: italic ? 'italic' : 'normal',
    }}
    aria-label={label}
  >
    {label}
  </span>
);
const PaymentRow = () => (
  <div className="flex items-center gap-2">
    <PayMark bg="#1A1F71" fg="#FFFFFF" label="VISA" italic />
    <PayMark bg="#000000" fg="#FFFFFF" label="MC" />
    <PayMark bg="#016FD0" fg="#FFFFFF" label="AMEX" />
    <PayMark bg="#00B0FF" fg="#FFFFFF" label="MP" />
  </div>
);

/* ──────────────────────────────────────────────────────────────────
 * Mobile accordion — wraps <details>. The +/− indicator rotates via
 * a sibling selector trick: `details[open] .indicator { rotate }`
 * is set in inline style so we don't depend on a global CSS rule.
 * ──────────────────────────────────────────────────────────────── */
const MobileAccordion = ({ title, children }) => (
  <details className="group border-t border-white/10 py-1">
    <summary
      // `list-none` removes the Firefox marker; the arbitrary variant
      // strips Safari/WebKit's separate disclosure triangle, which the
      // standard property doesn't cover.
      className="flex items-center justify-between cursor-pointer list-none py-3.5 select-none [&::-webkit-details-marker]:hidden"
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <span className="text-[11.5px] tracking-[0.22em] uppercase text-white/85 font-medium">
        {title}
      </span>
      {/* +/− indicator — uses Tailwind group/open if available, otherwise
          relies on the SVG line rotation below. We use two short lines so
          opening the accordion just hides the vertical one. */}
      <span aria-hidden="true" className="relative w-3 h-3 flex items-center justify-center">
        <span className="absolute inset-x-0 h-px bg-white/70 left-0 right-0 top-1/2 -translate-y-1/2" />
        <span className="absolute inset-y-0 w-px bg-white/70 top-0 bottom-0 left-1/2 -translate-x-1/2 transition-transform duration-200 group-open:scale-y-0" />
      </span>
    </summary>
    <ul className="flex flex-col gap-3 pb-4 pt-1">{children}</ul>
  </details>
);

const FooterLink = ({ to, children, external = false }) => {
  const cls =
    'text-[14px] text-white/80 hover:text-white transition-colors min-h-[24px] flex items-center';
  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={cls}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={cls}>
      {children}
    </Link>
  );
};

const Footer = (/* content prop intentionally ignored — labels are inline */) => {
  const { data: storeSettings } = usePublicStoreSettings();
  const WHATSAPP_DISPLAY = storeSettings?.['whatsapp.label'] || '+54 9 11 2400-1228';
  // Pre-filled message lands the conversation in Spanish and saves the
  // shopper a typing step on mobile.
  const WHATSAPP_URL = buildWhatsAppUrl(
    'Hola Pinksisly, me gustaría hacer una consulta.',
    storeSettings?.['whatsapp.number']
  );
  const EMAIL = 'pinksisly.info@gmail.com';
  const ADDRESS = 'Cuenca 621, CABA';
  const INSTA_URL = 'https://www.instagram.com/pinksislymoda/';
  const TIKTOK_URL = 'https://www.tiktok.com/@pinksislymoda';
  const year = new Date().getFullYear();

  // Reusable link sets — declared once, rendered twice (mobile accordion
  // + desktop column) so the source of truth stays in one place.
  // The first three mirror the owner-editable top-nav entries (Configuración
  // → Menú 1..3), same fallbacks as Navigation, so renaming the season there
  // renames it here too.
  const navLabel = (i, fallback) => {
    const v = storeSettings?.[`nav.item${i}.label`];
    return v && v.trim() ? v.trim() : fallback;
  };
  const navTo = (i, fallback) => {
    const v = storeSettings?.[`nav.item${i}.url`];
    return v && v.trim() ? v.trim() : fallback;
  };
  const TIENDA_LINKS = [
    { label: navLabel(1, 'Nuevo'), to: navTo(1, '/ropas?filter=nuevo') },
    { label: navLabel(2, 'Destacados'), to: navTo(2, '/ropas?orden=destacados') },
    { label: navLabel(3, 'SS2027'), to: navTo(3, '/ropas') },
    { label: 'Colecciones', to: '/colecciones' },
  ];
  const AYUDA_LINKS = [
    { label: 'Envíos', to: '/preguntas-frecuentes' },
    { label: 'Cambios', to: '/preguntas-frecuentes' },
    { label: 'Cómo comprar', to: '/metodos-de-pago' },
    { label: 'Contacto', to: '/contacto' },
  ];

  return (
    <footer className="bg-ink-primary text-white">
      {/* ════════════════════════════════════════════════════════════
       * MOBILE LAYOUT (<md). Hidden on md+.
       * ═══════════════════════════════════════════════════════════ */}
      <div className="md:hidden">
        {/* 1. Help hero — primary action above the fold of the footer */}
        <div className="px-6 pt-10 pb-7" style={{ background: 'rgba(255,255,255,0.025)' }}>
          <p className="text-[10.5px] tracking-[0.24em] uppercase text-white/55 mb-3">
            ¿Necesitás ayuda?
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full h-12 rounded-full bg-[#25D366] text-white font-medium text-[14px] tracking-[0.02em] hover:opacity-95 transition-opacity"
          >
            <WhatsAppGlyph size={17} />
            {WHATSAPP_DISPLAY}
          </a>
          <a
            href={`mailto:${EMAIL}`}
            className="flex items-center justify-center gap-2 mt-3 text-[13px] text-white/75 hover:text-white transition-colors break-all"
          >
            <MailGlyph size={13} /> {EMAIL}
          </a>
        </div>

        {/* 2. Accordion link sections */}
        <div className="px-6 pt-1">
          <MobileAccordion title="Tienda">
            {TIENDA_LINKS.map((l) => (
              <li key={l.label}>
                <FooterLink to={l.to}>{l.label}</FooterLink>
              </li>
            ))}
          </MobileAccordion>
          <MobileAccordion title="Ayuda">
            {AYUDA_LINKS.map((l) => (
              <li key={l.label}>
                <FooterLink to={l.to}>{l.label}</FooterLink>
              </li>
            ))}
          </MobileAccordion>

          {/* 3. Pinksisly contact — always open (3 lines, high-value info). */}
          <div className="border-t border-white/10 py-4">
            <p className="text-[11.5px] tracking-[0.22em] uppercase text-white/55 font-medium mb-3">
              Pinksisly
            </p>
            <ul className="flex flex-col gap-3">
              <li className="inline-flex items-center gap-2.5 text-[13.5px] text-white/80">
                <span className="text-white/55">
                  <PinGlyph />
                </span>{' '}
                {ADDRESS}
              </li>
            </ul>
          </div>
        </div>

        {/* 4. Brand block — compact, lower-priority on mobile. */}
        <div className="px-6 pt-6 pb-7 border-t border-white/10">
          <p className="font-display text-[32px] text-white leading-none mb-2">Pinksisly</p>
          <p className="text-[12.5px] text-white/55 leading-snug mb-5">Moda femenina argentina.</p>
          <div className="flex items-center gap-2.5">
            <a
              href={INSTA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-colors"
              aria-label="Instagram"
            >
              <InstaIcon />
            </a>
            <a
              href={TIKTOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-colors"
              aria-label="TikTok"
            >
              <TikTokIcon />
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-colors"
              aria-label="WhatsApp"
            >
              <WhatsAppGlyph />
            </a>
            <a
              href={`mailto:${EMAIL}`}
              className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-colors"
              aria-label="Email"
            >
              <MailGlyph />
            </a>
          </div>
        </div>

        {/* 5. Trust badges — single row (Cambios removed per business decision) */}
        <div className="px-6 py-5 border-t border-white/10 grid grid-cols-3 gap-x-3 gap-y-4">
          <TrustBadge
            icon={
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="1" y="4" width="15" height="13" rx="1" />
                <polygon points="16 8 20 8 23 11 23 17 16 17 16 8" />
                <circle cx="5.5" cy="18.5" r="2.5" />
                <circle cx="18.5" cy="18.5" r="2.5" />
              </svg>
            }
            title="Envío"
            sub="Todo Argentina"
          />
          <TrustBadge
            icon={
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="1" y="4" width="22" height="16" rx="2" />
                <line x1="1" y1="10" x2="23" y2="10" />
              </svg>
            }
            title="6 cuotas"
            sub="Sin interés"
          />
          <TrustBadge
            icon={
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
            }
            title="Compra segura"
            sub="Datos protegidos"
          />
        </div>

        {/* 6. Payments + copyright — single band */}
        <div className="px-6 py-5 border-t border-white/10 flex flex-col items-center gap-3">
          <PaymentRow />
          <p className="text-[11px] text-white/50 text-center">
            © {year} Pinksisly. Todos los derechos reservados.
          </p>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════
       * DESKTOP LAYOUT (md+). Hidden on mobile. Untouched from the
       * previous design — only the wrapper visibility class is new.
       * ═══════════════════════════════════════════════════════════ */}
      <div className="hidden md:block">
        {/* Top: brand + 4 link columns */}
        <div className="max-w-container mx-auto px-6 md:px-10 pt-14 md:pt-16 pb-10">
          <div className="grid grid-cols-12 gap-8">
            {/* Brand block */}
            <div className="col-span-4">
              <p className="font-display text-[48px] text-white leading-none mb-5">Pinksisly</p>
              <p className="text-[13px] text-white/65 leading-relaxed mb-6 max-w-[300px]">
                Moda femenina argentina.
                <br />
                Estilo sin esfuerzo, calidad que se siente.
              </p>
              <div className="flex items-center gap-3">
                <a
                  href={INSTA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-colors"
                  aria-label="Instagram"
                >
                  <InstaIcon />
                </a>
                <a
                  href={TIKTOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-colors"
                  aria-label="TikTok"
                >
                  <TikTokIcon />
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-colors"
                  aria-label="WhatsApp"
                >
                  <WhatsAppGlyph />
                </a>
                <a
                  href={`mailto:${EMAIL}`}
                  className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-colors"
                  aria-label="Email"
                >
                  <MailGlyph />
                </a>
              </div>
            </div>

            {/* Tienda */}
            <div className="col-span-2">
              <p className="text-[11px] tracking-[0.18em] uppercase text-white/55 mb-4">Tienda</p>
              <ul className="flex flex-col gap-2.5">
                {TIENDA_LINKS.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      className="text-[13px] text-white/80 hover:text-white transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Ayuda */}
            <div className="col-span-2">
              <p className="text-[11px] tracking-[0.18em] uppercase text-white/55 mb-4">Ayuda</p>
              <ul className="flex flex-col gap-2.5">
                {AYUDA_LINKS.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      className="text-[13px] text-white/80 hover:text-white transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pinksisly contact */}
            <div className="col-span-4">
              <p className="text-[11px] tracking-[0.18em] uppercase text-white/55 mb-4">
                Pinksisly
              </p>
              <ul className="flex flex-col gap-3">
                <li>
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2.5 text-[13px] text-white/80 hover:text-white transition-colors"
                  >
                    <span className="text-white/60">
                      <WhatsAppGlyph />
                    </span>{' '}
                    {WHATSAPP_DISPLAY}
                  </a>
                </li>
                <li>
                  <a
                    href={`mailto:${EMAIL}`}
                    className="inline-flex items-center gap-2.5 text-[13px] text-white/80 hover:text-white transition-colors break-all"
                  >
                    <span className="text-white/60 flex-shrink-0">
                      <MailGlyph />
                    </span>{' '}
                    {EMAIL}
                  </a>
                </li>
                <li className="inline-flex items-center gap-2.5 text-[13px] text-white/80">
                  <span className="text-white/60">
                    <PinGlyph />
                  </span>{' '}
                  {ADDRESS}
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Trust + payments strip — 4 columns now that "Cambios fáciles" was dropped */}
        <div className="border-t border-white/10">
          <div className="max-w-container mx-auto px-6 md:px-10 py-6 grid grid-cols-4 gap-6 items-center">
            <TrustBadge
              icon={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="1" y="4" width="15" height="13" rx="1" />
                  <polygon points="16 8 20 8 23 11 23 17 16 17 16 8" />
                  <circle cx="5.5" cy="18.5" r="2.5" />
                  <circle cx="18.5" cy="18.5" r="2.5" />
                </svg>
              }
              title="Envío a todo Argentina"
              sub="Rápido y seguro"
            />
            <TrustBadge
              icon={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="1" y="4" width="22" height="16" rx="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
              }
              title="6 cuotas sin interés"
              sub="Con tarjetas seleccionadas"
            />
            <TrustBadge
              icon={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
              }
              title="Compra 100% segura"
              sub="Tus datos protegidos"
            />
            <div className="justify-self-end">
              <PaymentRow />
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-white/10">
          <div className="max-w-container mx-auto px-6 md:px-10 py-5 text-center">
            <p className="text-[11.5px] text-white/55">
              © {year} Pinksisly. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
